<template>
  <el-dialog
    v-model="visible"
    :close-on-click-modal="false"
    :title="dialogTitle"
    modal-class="modal-class"
    :width="dialogWidth"
    :destroy-on-close="true"
    align-center
    @close="handleClose"
  >
    <div v-loading="loading || submitting" class="request-op-modal">
      <!-- 1. SINGLE-SELECT: UPDATE INFO -->
      <template v-if="opType === 'updateInfo'">
        <!-- Phase A: Choose insured person -->
        <div v-if="!selectedSingleInsured" class="request-op-select-list">
          <div class="request-op-toolbar d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div class="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
              <el-input
                v-model="searchQuery"
                class="request-op-search-input flex-grow-1"
                clearable
                :placeholder="$t('policySearchPlaceholder')"
                prefix-icon="Search"
              />
              <el-select
                v-model="filterGroupId"
                class="request-op-program-filter"
                clearable
                :placeholder="$t('commonProgram')"
                style="min-width: 170px; max-width: 230px;"
              >
                <el-option
                  v-for="item in availablePrograms"
                  :key="item.groupId"
                  :label="item.groupName"
                  :value="item.groupId"
                />
              </el-select>
            </div>
            <span class="text-xs text-muted text-nowrap">
              {{ $t("commonTotal") }}: <strong>{{ filteredInsureds.length }}</strong>
            </span>
          </div>

          <el-table
            :data="filteredInsureds"
            size="default"
            border
            class="el-table-css request-op-table"
            max-height="480"
            highlight-current-row
            @row-click="handleSingleSelect"
          >
            <el-table-column prop="insuredName" :label="$t('commonFullName')" min-width="200">
              <template #default="{ row }">
                <span class="fw-bold text-dark dark-text-white cursor-pointer">{{ row.insuredName }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="pin" label="PIN" width="110" align="center" />
            <el-table-column prop="cardFullNumber" :label="$t('commonCard')" width="140" align="center" />
            <el-table-column prop="groupName" :label="$t('commonProgram')" min-width="150" show-overflow-tooltip />
            <el-table-column :label="$t('commonStatus')" width="110" align="center">
              <template #default="{ row }">
                <span class="badge" :class="row.isEmployee ? 'badge--info' : 'badge--muted'">
                  {{ row.isEmployee ? $t("commonEmployee") : $t("commonFamilyMember") }}
                </span>
              </template>
            </el-table-column>
            <el-table-column width="90" align="center">
              <template #default="{ row }">
                <button
                  type="button"
                  class="action-btn action-btn--info action-btn--compact mb-0"
                  @click.stop="handleSingleSelect(row)"
                >
                  {{ $t("commonSelect") }}
                </button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- Phase B: Edit insured data -->
        <div v-else class="request-op-single-edit">
          <div class="request-op-selected-card soft-card mb-3 p-2 d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center gap-2">
              <i class="bi bi-person-badge text-info fs-4"></i>
              <div>
                <div class="fw-bold text-sm text-dark dark-text-white">{{ selectedSingleInsured.insuredName }}</div>
                <div class="text-xs text-muted">
                  <span v-if="selectedSingleInsured.pin">PIN: {{ selectedSingleInsured.pin }} &nbsp;|&nbsp;</span>
                  <span v-if="selectedSingleInsured.cardFullNumber">Kart: {{ selectedSingleInsured.cardFullNumber }} &nbsp;|&nbsp;</span>
                  <span v-if="selectedSingleInsured.groupName">Program: {{ selectedSingleInsured.groupName }}</span>
                </div>
              </div>
            </div>
            <button
              v-if="!isSingleMode"
              type="button"
              class="action-btn action-btn--muted action-btn--compact mb-0"
              @click="clearSingleSelect"
            >
              <i class="bi bi-arrow-left me-1"></i>
              <span>{{ $t("chooseAnotherInsured") }}</span>
            </button>
          </div>

          <InsuredAllDialog
            :info="selectedSingleInsured"
            @custom-event="onSingleUpdateSuccess"
          />
        </div>
      </template>

      <!-- 2. PROGRAM CHANGE -->
      <template v-else-if="opType === 'programChange'">
        <!-- Single Insured Mode (when opened from table row action) -->
        <div v-if="isSingleMode" class="request-op-single-card-mode">
          <div class="request-op-person-header soft-card p-3 mb-3 d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center gap-3">
              <div class="person-avatar bg-warning text-white rounded-circle d-flex align-items-center justify-content-center" style="width: 44px; height: 44px; font-size: 1.25rem;">
                <i class="bi bi-arrow-repeat"></i>
              </div>
              <div>
                <div class="fw-bold text-dark dark-text-white fs-6">
                  {{ singlePerson ? (singlePerson.insuredName || [singlePerson.lastName, singlePerson.firstName, singlePerson.middleName].filter(Boolean).join(" ")) : "" }}
                </div>
                <div class="text-xs text-muted d-flex align-items-center gap-2 flex-wrap mt-1">
                  <span v-if="singlePerson && singlePerson.pin"><strong>PIN:</strong> {{ singlePerson.pin }}</span>
                  <span v-if="singlePerson && singlePerson.cardFullNumber">&bull; <strong>Kart:</strong> {{ singlePerson.cardFullNumber }}</span>
                  <span v-if="singlePerson && singlePerson.groupName">&bull; <strong>Cari proqram:</strong> {{ singlePerson.groupName }}</span>
                  <span v-if="singlePerson" class="badge" :class="singlePerson.isEmployee ? 'badge--info' : 'badge--muted'">
                    {{ singlePerson.isEmployee ? $t("commonEmployee") : $t("commonFamilyMember") }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="soft-card p-3 mb-3">
            <div class="row g-3">
              <div class="col-md-6 col-12">
                <label class="form-label text-xs fw-bold">{{ $t("commonProgram") }} <span class="text-danger">*</span></label>
                <el-select
                  v-model="programForm.groupId"
                  class="w-100"
                  size="default"
                  :placeholder="$t('commonSelect')"
                >
                  <el-option
                    v-for="item in groupOptions"
                    :key="item.groupId"
                    :label="item.groupName"
                    :value="item.groupId"
                  />
                </el-select>
              </div>

              <div class="col-md-6 col-12">
                <label class="form-label text-xs fw-bold">{{ $t("insuredAcceptDate") }} <span class="text-danger">*</span></label>
                <el-date-picker
                  v-model="programForm.startDate"
                  type="date"
                  class="w-100"
                  size="default"
                  format="DD.MM.YYYY"
                  placeholder="dd.mm.yyyy"
                />
              </div>

              <div class="col-12">
                <label class="form-label text-xs fw-bold">{{ $t("commonNotes") }}</label>
                <el-input
                  v-model="programForm.descr"
                  type="textarea"
                  :rows="3"
                  :placeholder="$t('commonExtraNotes')"
                  resize="none"
                />
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-end gap-2 pt-2 border-top">
            <button
              type="button"
              class="action-btn action-btn--muted action-btn--compact mb-0"
              @click="handleClose"
            >
              {{ $t("commonCancel") }}
            </button>
            <button
              type="button"
              class="action-btn action-btn--warning action-btn--compact mb-0"
              :disabled="!programForm.groupId || !programForm.startDate || submitting"
              @click="submitBatchProgramChange"
            >
              <i class="bi bi-arrow-repeat me-1"></i>
              <span>{{ $t("programChange") }}</span>
            </button>
          </div>
        </div>

        <!-- Multi-Select Batch Mode (when opened globally) -->
        <div v-else class="row g-3">
          <!-- 1. LEFT COL: Insured selection table (First - 6 Cols) -->
          <div class="col-xl-6 col-lg-6 col-12">
            <div class="request-op-pane h-100">
              <div class="request-op-toolbar d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                <div class="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
                  <el-input
                    v-model="searchQuery"
                    class="request-op-search-input flex-grow-1"
                    clearable
                    :placeholder="$t('policySearchPlaceholder')"
                    prefix-icon="Search"
                  />
                  <el-select
                    v-model="filterGroupId"
                    class="request-op-program-filter"
                    clearable
                    :placeholder="$t('commonProgram')"
                    style="min-width: 150px; max-width: 200px;"
                  >
                    <el-option
                      v-for="item in availablePrograms"
                      :key="item.groupId"
                      :label="item.groupName"
                      :value="item.groupId"
                    />
                  </el-select>
                </div>
                <span class="text-xs text-muted text-nowrap">
                  {{ $t("commonTotal") }}: <strong>{{ filteredInsureds.length }}</strong>
                </span>
              </div>

              <el-table
                ref="programTableRef"
                :data="filteredInsureds"
                :row-key="getRowKey"
                size="small"
                border
                class="el-table-css request-op-table"
                max-height="520"
                @selection-change="handleSelectionChange"
                @row-click="onProgramRowClick"
              >
                <el-table-column type="selection" :reserve-selection="true" width="40" align="center" />
                <el-table-column prop="insuredName" :label="$t('commonFullName')" min-width="160" show-overflow-tooltip />
                <el-table-column prop="pin" label="PIN" width="90" align="center" />
                <el-table-column prop="cardFullNumber" :label="$t('commonCard')" width="105" align="center" />
                <el-table-column prop="groupName" :label="$t('commonProgram')" min-width="120" show-overflow-tooltip />
                <el-table-column :label="$t('commonStatus')" width="95" align="center">
                  <template #default="{ row }">
                    <span class="badge" :class="row.isEmployee ? 'badge--info' : 'badge--muted'">
                      {{ row.isEmployee ? $t("commonEmployee") : $t("commonFamilyMember") }}
                    </span>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>

          <!-- 2. MIDDLE COL: Selected Insureds Panel (3 Cols) -->
          <div class="col-xl-3 col-lg-3 col-12">
            <div class="request-op-sidebar soft-card p-3 h-100 d-flex flex-column">
              <div class="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                <span class="text-xs fw-bold text-dark dark-text-white d-flex align-items-center gap-1">
                  <i class="bi bi-people-fill text-warning"></i>
                  <span>{{ $t("selectedPersonsList") }}</span>
                  <span class="badge badge--warning ms-1">{{ selectedRows.length }}</span>
                </span>
                <button
                  v-if="selectedRows.length > 0"
                  type="button"
                  class="btn-clear-pill"
                  :title="$t('commonClear')"
                  @click="clearAllSelected"
                >
                  <i class="bi bi-trash3 me-1"></i>
                  <span>{{ $t("commonClear") }}</span>
                </button>
              </div>

              <!-- Warning if some selected persons already have this program -->
              <div
                v-if="opType === 'programChange' && sameProgramCount > 0"
                class="request-op-warning-banner mb-2"
              >
                <div class="warning-banner-content">
                  <div class="warning-banner-icon">
                    <i class="bi bi-exclamation-triangle-fill"></i>
                  </div>
                  <div class="warning-banner-text">
                    <span class="warning-count">{{ sameProgramCount }}</span> {{ $t("sameProgramWarning") }}
                  </div>
                </div>
                <button
                  type="button"
                  class="warning-banner-btn"
                  @click="removeSameProgramPersons"
                >
                  <i class="bi bi-dash-circle"></i>
                  <span>{{ $t("removeSameProgramAction") }}</span>
                </button>
              </div>

              <!-- Scrollable list of selected cards -->
              <div v-if="selectedRows.length > 0" class="selected-cards-list flex-grow-1">
                <div
                  v-for="person in selectedRows"
                  :key="person.cardId || person.pin"
                  class="selected-person-card selected-person-card--warning mb-2 p-2"
                >
                  <div class="d-flex justify-content-between align-items-center gap-2">
                    <div class="selected-person-card__main min-w-0 flex-grow-1">
                      <div class="selected-person-card__name fw-bold text-xs text-truncate" :title="person.insuredName">
                        {{ person.insuredName || [person.lastName, person.firstName].filter(Boolean).join(" ") }}
                      </div>
                      <div class="selected-person-card__meta text-muted text-xs text-truncate">
                        {{ person.groupName || "-" }}
                      </div>
                      <div v-if="opType === 'programChange' && isSameProgram(person)" class="mt-1">
                        <span class="badge bg-warning text-dark text-xs" style="font-size: 0.68rem;">
                          <i class="bi bi-exclamation-circle-fill me-1"></i>{{ $t("alreadyHasThisProgram") }}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      class="selected-person-card__remove"
                      :title="$t('commonDelete')"
                      @click.stop="removeSelected(person)"
                    >
                      <i class="bi bi-x"></i>
                    </button>
                  </div>
                </div>
              </div>
              <div v-else class="selected-cards-empty flex-grow-1 d-flex flex-column align-items-center justify-content-center py-4 text-center">
                <i class="bi bi-people text-muted fs-3 mb-2 opacity-50"></i>
                <div class="text-xs text-muted">{{ $t("selectAtLeastOne") }}</div>
              </div>
            </div>
          </div>

          <!-- 3. RIGHT COL: Form parameters -->
          <div class="col-xl-3 col-lg-3 col-12">
            <div class="request-op-form-card soft-card p-3 h-100 d-flex flex-column justify-content-between">
              <div class="request-op-form-body">
                <h6 class="text-sm fw-bold mb-3 pb-2 border-bottom text-warning d-flex align-items-center gap-1">
                  <i class="bi bi-arrow-left-right"></i>
                  <span>{{ $t("programOperations") }}</span>
                </h6>

                <div class="mb-3">
                  <label class="form-label text-xs fw-bold">{{ $t("commonProgram") }} <span class="text-danger">*</span></label>
                  <el-select
                    v-model="programForm.groupId"
                    class="w-100"
                    size="default"
                    :placeholder="$t('commonSelect')"
                  >
                    <el-option
                      v-for="item in groupOptions"
                      :key="item.groupId"
                      :label="item.groupName"
                      :value="item.groupId"
                    />
                  </el-select>
                </div>

                <div class="mb-3">
                  <label class="form-label text-xs fw-bold">{{ $t("insuredAcceptDate") }} <span class="text-danger">*</span></label>
                  <el-date-picker
                    v-model="programForm.startDate"
                    type="date"
                    class="w-100"
                    size="default"
                    format="DD.MM.YYYY"
                    placeholder="dd.mm.yyyy"
                  />
                </div>

                <div class="mb-2">
                  <label class="form-label text-xs fw-bold">{{ $t("commonNotes") }}</label>
                  <el-input
                    v-model="programForm.descr"
                    type="textarea"
                    :rows="4"
                    :placeholder="$t('commonExtraNotes')"
                    resize="none"
                  />
                </div>
              </div>

              <div class="request-op-actions mt-3 pt-3 border-top d-flex justify-content-end gap-2">
                <button
                  type="button"
                  class="action-btn action-btn--muted action-btn--compact mb-0"
                  @click="handleClose"
                >
                  {{ $t("commonCancel") }}
                </button>
                <button
                  type="button"
                  class="action-btn action-btn--warning action-btn--compact mb-0"
                  :disabled="selectedRows.length === 0 || !programForm.groupId || !programForm.startDate || submitting"
                  @click="submitBatchProgramChange"
                >
                  <i class="bi bi-arrow-left-right me-1"></i>
                  <span>{{ $t("commonConfirm") }} ({{ selectedRows.length }})</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- 3. TERMINATION -->
      <template v-else-if="opType === 'terminate'">
        <!-- Single Insured Mode (when opened from table row action) -->
        <div v-if="isSingleMode" class="request-op-single-card-mode">
          <div class="request-op-person-header soft-card p-3 mb-3 d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center gap-3">
              <div class="person-avatar bg-danger text-white rounded-circle d-flex align-items-center justify-content-center" style="width: 44px; height: 44px; font-size: 1.25rem;">
                <i class="bi bi-person-x-fill"></i>
              </div>
              <div>
                <div class="fw-bold text-dark dark-text-white fs-6">
                  {{ singlePerson ? (singlePerson.insuredName || [singlePerson.lastName, singlePerson.firstName, singlePerson.middleName].filter(Boolean).join(" ")) : "" }}
                </div>
                <div class="text-xs text-muted d-flex align-items-center gap-2 flex-wrap mt-1">
                  <span v-if="singlePerson && singlePerson.pin"><strong>PIN:</strong> {{ singlePerson.pin }}</span>
                  <span v-if="singlePerson && singlePerson.cardFullNumber">&bull; <strong>Kart:</strong> {{ singlePerson.cardFullNumber }}</span>
                  <span v-if="singlePerson && singlePerson.groupName">&bull; <strong>Proqram:</strong> {{ singlePerson.groupName }}</span>
                  <span v-if="singlePerson" class="badge" :class="singlePerson.isEmployee ? 'badge--info' : 'badge--muted'">
                    {{ singlePerson.isEmployee ? $t("commonEmployee") : $t("commonFamilyMember") }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="soft-card p-3 mb-3">
            <div class="row g-3">
              <div class="col-12">
                <label class="form-label text-xs fw-bold">{{ $t("insuredCancelDate") }} <span class="text-danger">*</span></label>
                <el-date-picker
                  v-model="cancelForm.cancelDate"
                  type="date"
                  class="w-100"
                  size="default"
                  format="DD.MM.YYYY"
                  placeholder="dd.mm.yyyy"
                />
              </div>

              <div class="col-12">
                <label class="form-label text-xs fw-bold">{{ $t("commonNotes") }}</label>
                <el-input
                  v-model="cancelForm.descr"
                  type="textarea"
                  :rows="4"
                  :placeholder="$t('commonExtraNotes')"
                  resize="none"
                />
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-end gap-2 pt-2 border-top">
            <button
              type="button"
              class="action-btn action-btn--muted action-btn--compact mb-0"
              @click="handleClose"
            >
              {{ $t("commonCancel") }}
            </button>
            <button
              type="button"
              class="action-btn action-btn--danger action-btn--compact mb-0"
              :disabled="!cancelForm.cancelDate || submitting"
              @click="submitBatchTermination"
            >
              <i class="bi bi-x-circle me-1"></i>
              <span>{{ $t("insuredTerminate") }}</span>
            </button>
          </div>
        </div>

        <!-- Multi-Select Batch Mode (when opened globally) -->
        <div v-else class="row g-3">
          <!-- 1. LEFT COL: Insured selection table (First - 6 Cols) -->
          <div class="col-xl-6 col-lg-6 col-12">
            <div class="request-op-pane h-100">
              <div class="request-op-toolbar d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                <div class="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
                  <el-input
                    v-model="searchQuery"
                    class="request-op-search-input flex-grow-1"
                    clearable
                    :placeholder="$t('policySearchPlaceholder')"
                    prefix-icon="Search"
                  />
                  <el-select
                    v-model="filterGroupId"
                    class="request-op-program-filter"
                    clearable
                    :placeholder="$t('commonProgram')"
                    style="min-width: 150px; max-width: 200px;"
                  >
                    <el-option
                      v-for="item in availablePrograms"
                      :key="item.groupId"
                      :label="item.groupName"
                      :value="item.groupId"
                    />
                  </el-select>
                </div>
                <span class="text-xs text-muted text-nowrap">
                  {{ $t("commonTotal") }}: <strong>{{ filteredInsureds.length }}</strong>
                </span>
              </div>

              <el-table
                ref="cancelTableRef"
                :data="filteredInsureds"
                :row-key="getRowKey"
                size="small"
                border
                class="el-table-css request-op-table"
                max-height="520"
                @selection-change="handleSelectionChange"
                @row-click="onCancelRowClick"
              >
                <el-table-column type="selection" :reserve-selection="true" width="40" align="center" />
                <el-table-column prop="insuredName" :label="$t('commonFullName')" min-width="160" show-overflow-tooltip />
                <el-table-column prop="pin" label="PIN" width="90" align="center" />
                <el-table-column prop="cardFullNumber" :label="$t('commonCard')" width="105" align="center" />
                <el-table-column prop="groupName" :label="$t('commonProgram')" min-width="120" show-overflow-tooltip />
                <el-table-column :label="$t('commonStatus')" width="95" align="center">
                  <template #default="{ row }">
                    <span class="badge" :class="row.isEmployee ? 'badge--info' : 'badge--muted'">
                      {{ row.isEmployee ? $t("commonEmployee") : $t("commonFamilyMember") }}
                    </span>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>

          <!-- 2. MIDDLE COL: Selected Insureds Panel (3 Cols) -->
          <div class="col-xl-3 col-lg-3 col-12">
            <div class="request-op-sidebar soft-card p-3 h-100 d-flex flex-column">
              <div class="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                <span class="text-xs fw-bold text-dark dark-text-white d-flex align-items-center gap-1">
                  <i class="bi bi-people-fill text-danger"></i>
                  <span>{{ $t("selectedPersonsList") }}</span>
                  <span class="badge badge--danger ms-1">{{ selectedRows.length }}</span>
                </span>
                <button
                  v-if="selectedRows.length > 0"
                  type="button"
                  class="btn-clear-pill"
                  :title="$t('commonClear')"
                  @click="clearAllSelected"
                >
                  <i class="bi bi-trash3 me-1"></i>
                  <span>{{ $t("commonClear") }}</span>
                </button>
              </div>

              <!-- Scrollable list of selected cards -->
              <div v-if="selectedRows.length > 0" class="selected-cards-list flex-grow-1">
                <div
                  v-for="person in selectedRows"
                  :key="person.cardId || person.pin"
                  class="selected-person-card selected-person-card--danger mb-2 p-2"
                >
                  <div class="d-flex justify-content-between align-items-center gap-2">
                    <div class="selected-person-card__main min-w-0">
                      <div class="selected-person-card__name fw-bold text-xs text-truncate" :title="person.insuredName">
                        {{ person.insuredName || [person.lastName, person.firstName].filter(Boolean).join(" ") }}
                      </div>
                    </div>
                    <button
                      type="button"
                      class="selected-person-card__remove"
                      :title="$t('commonDelete')"
                      @click.stop="removeSelected(person)"
                    >
                      <i class="bi bi-x"></i>
                    </button>
                  </div>
                </div>
              </div>
              <div v-else class="selected-cards-empty flex-grow-1 d-flex flex-column align-items-center justify-content-center py-4 text-center">
                <i class="bi bi-people text-muted fs-3 mb-2 opacity-50"></i>
                <div class="text-xs text-muted">{{ $t("selectAtLeastOne") }}</div>
              </div>
            </div>
          </div>

          <!-- 3. RIGHT COL: Termination Form Parameters -->
          <div class="col-xl-3 col-lg-3 col-12">
            <div class="request-op-form-card soft-card p-3 h-100 d-flex flex-column justify-content-between">
              <div class="request-op-form-body">
                <h6 class="text-sm fw-bold mb-3 pb-2 border-bottom text-danger d-flex align-items-center gap-1">
                  <i class="bi bi-x-circle"></i>
                  <span>{{ $t("terminateOperations") }}</span>
                </h6>

                <div class="mb-3">
                  <label class="form-label text-xs fw-bold">{{ $t("insuredCancelDate") }} <span class="text-danger">*</span></label>
                  <el-date-picker
                    v-model="cancelForm.cancelDate"
                    type="date"
                    class="w-100"
                    size="default"
                    format="DD.MM.YYYY"
                    placeholder="dd.mm.yyyy"
                  />
                </div>

                <div class="mb-2">
                  <label class="form-label text-xs fw-bold">{{ $t("commonNotes") }}</label>
                  <el-input
                    v-model="cancelForm.descr"
                    type="textarea"
                    :rows="6"
                    :placeholder="$t('commonExtraNotes')"
                    resize="none"
                  />
                </div>
              </div>

              <div class="request-op-actions mt-3 pt-3 border-top d-flex justify-content-end gap-2">
                <button
                  type="button"
                  class="action-btn action-btn--muted action-btn--compact mb-0"
                  @click="handleClose"
                >
                  {{ $t("commonCancel") }}
                </button>
                <button
                  type="button"
                  class="action-btn action-btn--danger action-btn--compact mb-0"
                  :disabled="selectedRows.length === 0 || !cancelForm.cancelDate || submitting"
                  @click="submitBatchTermination"
                >
                  <i class="bi bi-x-circle me-1"></i>
                  <span>{{ $t("insuredTerminate") }} ({{ selectedRows.length }})</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </el-dialog>

  <!-- Family Members Termination Warning Dialog (Horizontal Layout) -->
  <el-dialog
    v-model="familyWarningModalVisible"
    :title="$t('familyMembersWarningTitle') || 'Ailə üzvləri ilə bağlı xəbərdarlıq və fərdi idarəetmə'"
    width="1080px"
    append-to-body
    destroy-on-close
    align-center
    class="family-termination-dialog"
  >
    <div class="family-term-container">
      <div class="row g-3">
        <!-- Left Column: Master Options, Global Presets & Summary -->
        <div class="col-12 col-lg-5 d-flex flex-column">
          <!-- Top Warning Banner -->
          <div class="family-warning-banner p-3 rounded-3 mb-3">
            <div class="d-flex align-items-start gap-2">
              <div class="text-danger fs-4 flex-shrink-0">
                <i class="bi bi-exclamation-triangle-fill"></i>
              </div>
              <div class="flex-grow-1">
                <div class="fw-bold text-danger fs-6 mb-1">
                  {{ familyGroupsToHandle.length }} işçiyə bağlı {{ allDependantsFlat.length }} ailə üzvü var!
                </div>
                <div class="text-xs family-warning-banner__sub">
                  Sol paneldən ümumi qayda seçin və ya sağ paneldə hər ailə üzvünü fərdi şəkildə istədiyiniz işçiyə bağlayın / xitam verin.
                </div>
              </div>
            </div>
          </div>

          <!-- Master Action Options -->
          <label class="form-label text-xs fw-bold text-uppercase family-members-box__label mb-2">
            Ümumi Qaydalar / Rejimlər:
          </label>
          <div class="d-flex flex-column gap-2 mb-3">
            <!-- Preset 1: Terminate All -->
            <div
              class="family-option-card family-option-card--danger"
              :class="{ 'is-active': familyActionPreset === 'terminateAll' }"
              @click="applyGlobalPreset('terminateAll')"
            >
              <div class="d-flex align-items-center gap-2">
                <el-radio v-model="familyActionPreset" label="terminateAll">
                  <span class="family-option-title family-option-title--danger">
                    1. Bütün ailə üzvləri ilə xitam ver
                  </span>
                </el-radio>
              </div>
              <p class="family-option-desc ms-4 mt-1 mb-0">
                Bütün ailə üzvləri işçilərlə bərabər xitam olunur.
              </p>
            </div>

            <!-- Preset 2: Granular Custom Control -->
            <div
              class="family-option-card family-option-card--info"
              :class="{ 'is-active': familyActionPreset === 'custom' }"
              @click="applyGlobalPreset('custom')"
            >
              <div class="d-flex align-items-center justify-content-between">
                <el-radio v-model="familyActionPreset" label="custom">
                  <span class="family-option-title family-option-title--info">
                    2. Fərdi tənzimləmə
                  </span>
                </el-radio>
                <span class="badge bg-info text-white text-xs">
                  Sağ paneldə seçin
                </span>
              </div>
              <p class="family-option-desc ms-4 mt-1 mb-0">
                Sağ paneldən hər ailəni və ya üzvü ayrıca təyin edin: kimin xitam ediləcəyini və ya kimin hansı işçiyə bağlanacağını sərbəst seçin.
              </p>
            </div>
          </div>

          <!-- Summary Box -->
          <div class="family-summary-box p-3 rounded-3 mt-auto border">
            <div class="text-xs fw-bold text-muted text-uppercase mb-2">Əməliyyat Xülasəsi:</div>
            <div class="d-flex justify-content-between text-xs py-1 border-bottom">
              <span>Xitam veriləcək işçilər:</span>
              <strong class="text-danger">{{ selectedRowsCount }} nəfər</strong>
            </div>
            <div class="d-flex justify-content-between text-xs py-1 border-bottom">
              <span>Xitam veriləcək ailə üzvləri:</span>
              <strong class="text-danger">{{ dependantsToTerminateCount }} nəfər</strong>
            </div>
            <div class="d-flex justify-content-between text-xs py-1 border-bottom">
              <span>Yeni işçiyə bağlanan ailə üzvləri:</span>
              <strong class="text-primary">{{ dependantsToReassignCount }} nəfər</strong>
            </div>
            <div class="d-flex justify-content-between text-xs pt-2 fw-bold">
              <span>Cəmi xitam sorğusu:</span>
              <span class="badge bg-danger text-white fs-7">{{ totalTerminatingCount }} nəfər</span>
            </div>
          </div>
        </div>

        <!-- Right Column: Granular Per-Family / Per-Dependant interactive list -->
        <div class="col-12 col-lg-7">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <label class="form-label text-xs fw-bold text-uppercase family-members-box__label mb-0">
              Ailə üzvlərinin fərdi təyinatı:
            </label>
            <div class="family-action-switch family-action-switch--sm">
              <button
                type="button"
                title="Bütün ailə üzvlərinə xitam ver"
                class="family-switch-btn family-switch-btn--danger"
                :class="{ 'is-active': isAllDependantsAction('terminate') }"
                @click="setAllDependantsAction('terminate')"
              >
                <i class="bi bi-x-circle-fill me-1"></i>Hamısına xitam
              </button>
              <button
                type="button"
                title="Bütün ailə üzvlərini yeni işçiyə bağla"
                class="family-switch-btn family-switch-btn--primary"
                :class="{ 'is-active': isAllDependantsAction('reassign') }"
                @click="setAllDependantsAction('reassign')"
              >
                <i class="bi bi-arrow-left-right me-1"></i>Hamısını bağla
              </button>
            </div>
          </div>

          <div class="family-groups-scrollable" style="max-height: 480px; overflow-y: auto; padding-right: 4px;">
            <div
              v-for="group in familyGroupsToHandle"
              :key="group.employee.cardId || group.employee.pin"
              class="family-group-panel p-3 mb-3 rounded-3 border"
            >
              <!-- Employee Card Header -->
              <div class="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom flex-wrap gap-2">
                <div class="d-flex align-items-center gap-2">
                  <span class="badge bg-primary text-white text-xs">İşçi</span>
                  <strong class="text-xs">{{ group.employee.insuredName }}</strong>
                  <span v-if="group.employee.pin" class="text-xxs text-muted">PIN: {{ group.employee.pin }}</span>
                </div>
                <!-- Quick Group Buttons -->
                <div class="d-flex align-items-center gap-2">
                  <span class="text-xxs text-muted">{{ group.dependants.length }} ailə üzvü:</span>
                  <div class="family-action-switch family-action-switch--sm">
                    <button
                      type="button"
                      title="Bu ailənin bütün üzvlərinə xitam ver"
                      class="family-switch-btn family-switch-btn--danger"
                      :class="{ 'is-active': isGroupAllAction(group, 'terminate') }"
                      @click="setGroupAction(group, 'terminate')"
                    >
                      <i class="bi bi-x-circle-fill me-1"></i>Xitam
                    </button>
                    <button
                      type="button"
                      title="Bu ailənin bütün üzvlərini yeni işçiyə bağla"
                      class="family-switch-btn family-switch-btn--primary"
                      :class="{ 'is-active': isGroupAllAction(group, 'reassign') }"
                      @click="setGroupAction(group, 'reassign')"
                    >
                      <i class="bi bi-arrow-left-right me-1"></i>Bağla
                    </button>
                  </div>
                </div>
              </div>

              <!-- Group target employee picker (for batch reassigning this specific family) -->
              <div
                v-if="isGroupHavingReassign(group)"
                class="group-transfer-picker p-2 mb-2 rounded-2 border"
              >
                <div class="d-flex align-items-center gap-2">
                  <span class="text-xxs fw-bold text-primary flex-shrink-0">
                    <i class="bi bi-arrow-return-right me-1"></i>Bu ailə üçün yeni işçi:
                  </span>
                  <el-select
                    :model-value="getGroupTargetEmployee(group)"
                    filterable
                    value-key="cardId"
                    placeholder="Bu ailəni bağlamaq üçün işçi seçin..."
                    size="small"
                    class="flex-grow-1"
                    @change="(val) => setGroupTargetEmployee(group, val)"
                  >
                    <el-option
                      v-for="emp in activeEmployeesForTransfer"
                      :key="emp.cardId"
                      :label="emp.insuredName"
                      :value="emp"
                    />
                  </el-select>
                </div>
              </div>

              <!-- Individual Dependants Rows -->
              <div class="dependants-list d-flex flex-column gap-2">
                <div
                  v-for="dep in group.dependants"
                  :key="getDependantKey(dep)"
                  class="dependant-row-card p-2 rounded-2 border d-flex flex-column gap-1"
                  :class="{
                    'dependant-row-card--terminate': getDepAction(dep) === 'terminate',
                    'dependant-row-card--reassign': getDepAction(dep) === 'reassign'
                  }"
                >
                  <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div class="d-flex align-items-center gap-2 min-w-0">
                      <i
                        :class="getDepAction(dep) === 'terminate' ? 'bi bi-person-x-fill text-danger' : 'bi bi-person-check-fill text-primary'"
                      ></i>
                      <span class="text-xs fw-bold text-truncate">{{ dep.dependantInsuredName || dep.insuredName }}</span>
                      <span v-if="dep.pin || dep.dependantPin" class="text-xxs text-muted">
                        PIN: {{ dep.pin || dep.dependantPin }}
                      </span>
                    </div>

                    <!-- Modern Action Segmented Toggle -->
                    <div class="family-action-switch">
                      <button
                        type="button"
                        class="family-switch-btn family-switch-btn--danger"
                        :class="{ 'is-active': getDepAction(dep) === 'terminate' }"
                        @click="setDependantAction(dep, 'terminate')"
                      >
                        <i class="bi bi-x-circle-fill me-1"></i>Xitam
                      </button>
                      <button
                        type="button"
                        class="family-switch-btn family-switch-btn--primary"
                        :class="{ 'is-active': getDepAction(dep) === 'reassign' }"
                        @click="setDependantAction(dep, 'reassign')"
                      >
                        <i class="bi bi-arrow-left-right me-1"></i>Bağla
                      </button>
                    </div>
                  </div>

                  <!-- If Reassign & Group has target selected: show clean linked badge with optional override -->
                  <div
                    v-if="getDepAction(dep) === 'reassign' && getGroupTargetEmployee(group)"
                    class="d-flex align-items-center justify-content-between gap-1 pt-1 border-top"
                  >
                    <div class="d-flex align-items-center gap-1 min-w-0">
                      <span class="text-xxs text-muted"><i class="bi bi-link-45deg me-1"></i>Bağlanır:</span>
                      <span class="badge bg-primary text-white text-xxs text-truncate" style="max-width: 220px;">
                        {{ (getEffectiveDepTarget(dep, group) && getEffectiveDepTarget(dep, group).insuredName) || getGroupTargetEmployee(group).insuredName }}
                      </span>
                    </div>
                    <button
                      type="button"
                      class="btn-group-pill btn-group-pill--primary"
                      style="padding: 2px 7px; font-size: 0.65rem;"
                      @click="getDepState(dep).customOverride = !getDepState(dep).customOverride"
                    >
                      <i class="bi bi-pencil-square me-1"></i>{{ getDepState(dep).customOverride ? 'Qrupa bağla' : 'Fərdi dəyiş' }}
                    </button>
                  </div>

                  <!-- If Individual Override OR No Group Target Selected: show individual target picker -->
                  <div
                    v-if="getDepAction(dep) === 'reassign' && (!getGroupTargetEmployee(group) || getDepState(dep).customOverride)"
                    class="d-flex align-items-center gap-2 pt-1"
                    :class="{ 'border-top': !getGroupTargetEmployee(group) }"
                  >
                    <span class="text-xxs text-muted flex-shrink-0">
                      Fərdi işçi:
                    </span>
                    <el-select
                      v-model="getDepState(dep).targetEmployee"
                      filterable
                      value-key="cardId"
                      placeholder="Fərdi işçi seçin..."
                      size="small"
                      class="flex-grow-1"
                    >
                      <el-option
                        v-for="emp in activeEmployeesForTransfer"
                        :key="emp.cardId"
                        :label="emp.insuredName"
                        :value="emp"
                      />
                    </el-select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer Buttons -->
      <div class="d-flex justify-content-between align-items-center gap-2 pt-3 mt-3 border-top flex-wrap">
        <div class="text-xs">
          <span v-if="!isFamilyActionValid" class="text-warning">
            <i class="bi bi-exclamation-triangle me-1"></i>Zəhmət olmasa "Bağla" seçilmiş bütün ailə üzvləri üçün yeni işçi seçin.
          </span>
          <span v-else class="text-success">
            <i class="bi bi-check-circle-fill me-1"></i>Seçimlər tamamlandı və icraya hazırdır.
          </span>
        </div>
        <div class="d-flex gap-2">
          <button
            type="button"
            class="action-btn action-btn--muted action-btn--compact"
            @click="familyWarningModalVisible = false"
          >
            {{ $t("commonCancel") }}
          </button>
          <button
            type="button"
            class="action-btn action-btn--danger action-btn--compact"
            :disabled="!isFamilyActionValid || submitting"
            @click="executeFamilyTerminationAction"
          >
            <i class="bi bi-check2-all me-1"></i>
            <span>Təsdiq et və icra et ({{ totalTerminatingCount }} xitam, {{ dependantsToReassignCount }} bağlama)</span>
          </button>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { getPolicyId } from "@/project/Source/routes";
import Source from "@/project/Source/claim.js";
import CryptoJS from "crypto-js";
import { swalDialogClass } from "@/project/Source/helper.js";
import { notifyRequestSuccess } from "@/project/Source/utils.js";
import InsuredAllDialog from "@/project/Insured/components/InsuredAllDialog.vue";

export default {
  name: "RequestOperationModal",
  components: {
    InsuredAllDialog,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    opType: {
      type: String,
      default: "updateInfo", // 'updateInfo', 'programChange', 'terminate'
    },
    policyId: {
      type: [Number, String],
      default: 0,
    },
    initialSelectedInsured: {
      type: Object,
      default: null,
    },
  },
  emits: ["update:modelValue", "success", "close"],
  data() {
    return {
      insuredList: [],
      loading: false,
      submitting: false,
      searchQuery: "",
      filterGroupId: null,
      selectedRows: [],
      selectedSingleInsured: null,
      groupOptions: [],
      statusOptions: [
        { value: true, text: "işçi" },
        { value: false, text: "ailə üzvü" },
      ],
      programForm: {
        groupId: null,
        startDate: new Date(),
        descr: "",
      },
      cancelForm: {
        cancelDate: new Date(),
        descr: "",
      },
      familyWarningModalVisible: false,
      familyGroupsToHandle: [],
      allDependantsFlat: [],
      familyActionPreset: "terminateAll",
      globalTransferEmployee: null,
      dependantActionsMap: {},
      groupTransferEmployees: {},
      personToTerminate: null,
      dependantsToHandle: [],
      activeEmployeesForTransfer: [],
    };
  },
  computed: {
    isSingleEmployeeWithDeps() {
      return this.familyGroupsToHandle && this.familyGroupsToHandle.length === 1;
    },
    dependantsToTerminateCount() {
      return (this.allDependantsFlat || []).filter(
        (dep) => this.getDepAction(dep) === "terminate"
      ).length;
    },
    dependantsToReassignCount() {
      return (this.allDependantsFlat || []).filter(
        (dep) => this.getDepAction(dep) === "reassign"
      ).length;
    },
    totalTerminatingCount() {
      return this.selectedRowsCount;
    },
    isFamilyActionValid() {
      if (this.dependantsToReassignCount === 0) return true;
      for (const group of this.familyGroupsToHandle || []) {
        for (const dep of group.dependants || []) {
          if (this.getDepAction(dep) === "reassign") {
            const target = this.getEffectiveDepTarget(dep, group);
            if (!target || !target.cardId) {
              return false;
            }
          }
        }
      }
      return true;
    },
    totalPersonsToTerminate() {
      const rows = this.selectedRows.length > 0 ? this.selectedRows : (this.singlePerson ? [this.singlePerson] : []);
      return rows.length + (this.allDependantsFlat ? this.allDependantsFlat.length : 0);
    },
    selectedRowsCount() {
      const rows = this.selectedRows.length > 0 ? this.selectedRows : (this.singlePerson ? [this.singlePerson] : []);
      return rows.length;
    },
    visible: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit("update:modelValue", val);
      },
    },
    dialogTitle() {
      if (this.opType === "updateInfo") {
        return this.$t("updateInfoTitle");
      }
      if (this.opType === "programChange") {
        return this.$t("programOperations");
      }
      if (this.opType === "terminate") {
        return this.$t("terminateOperations");
      }
      return "";
    },
    isSingleMode() {
      return !!this.initialSelectedInsured;
    },
    singlePerson() {
      return this.selectedSingleInsured || (this.selectedRows && this.selectedRows[0]) || this.initialSelectedInsured || null;
    },
    dialogWidth() {
      if (this.isSingleMode) {
        if (this.opType === "updateInfo") return "1120px";
        if (this.opType === "programChange") return "680px";
        if (this.opType === "terminate") return "580px";
      }
      if (this.opType === "updateInfo") {
        return "1120px";
      }
      return "min(1720px, 98vw)";
    },
    availablePrograms() {
      if (this.groupOptions && this.groupOptions.length) {
        return this.groupOptions;
      }
      const map = new Map();
      (this.insuredList || []).forEach((item) => {
        const gId = item.groupId || item.id;
        const gName = item.groupName || item.name;
        if (gId && gName && !map.has(gId)) {
          map.set(gId, { groupId: gId, groupName: gName });
        }
      });
      return Array.from(map.values());
    },
    filteredInsureds() {
      let list = this.insuredList || [];
      if (this.filterGroupId) {
        list = list.filter((item) => Number(item.groupId) === Number(this.filterGroupId));
      }
      if (this.searchQuery) {
        const q = this.searchQuery.trim().toLowerCase();
        list = list.filter((item) => {
          const name = (item.insuredName || "").toLowerCase();
          const pin = (item.pin || "").toLowerCase();
          const card = (item.cardFullNumber || "").toLowerCase();
          const group = (item.groupName || "").toLowerCase();
          return name.includes(q) || pin.includes(q) || card.includes(q) || group.includes(q);
        });
      }
      return list;
    },
    sameProgramCount() {
      if (this.opType !== "programChange" || !this.programForm.groupId) return 0;
      return this.selectedRows.filter(
        (item) => Number(item.groupId) === Number(this.programForm.groupId)
      ).length;
    },
  },
  watch: {
    modelValue(val) {
      if (val) {
        this.resetState();
        this.loadData();
      }
    },
  },
  methods: {
    getEffectivePolicyId() {
      const fromProp = Number(this.policyId);
      if (fromProp) return fromProp;
      const fromRoute = Number(getPolicyId(this.$route));
      if (fromRoute) return fromRoute;
      const fromStorage = Number(JSON.parse(sessionStorage.getItem("policy") || "0"));
      if (fromStorage) return fromStorage;
      return 0;
    },
    getUserId() {
      try {
        const raw = window.localStorage.getItem("user");
        if (!raw) return 0;
        return JSON.parse(
          CryptoJS.AES.decrypt(raw, "secret_key").toString(CryptoJS.enc.Utf8)
        ).userId;
      } catch (e) {
        return 0;
      }
    },
    resetState() {
      this.searchQuery = "";
      this.filterGroupId = null;
      this.selectedRows = this.initialSelectedInsured ? [this.initialSelectedInsured] : [];
      this.selectedSingleInsured = this.initialSelectedInsured || null;
      this.submitting = false;
      this.programForm = {
        groupId: this.initialSelectedInsured?.groupId || null,
        startDate: new Date(),
        isEmployee: this.initialSelectedInsured?.isEmployee !== false,
        descr: "",
      };
      this.cancelForm = {
        cancelDate: new Date(),
        descr: "",
      };
    },
    loadData() {
      this.loading = true;
      const policyId = this.getEffectivePolicyId();

      const list = {
        requestCorrelationId: "string",
        data: {
          policyId: Number(policyId),
          totalCount: 0,
          totalInusred: 0,
          search: "",
          currentPage: 1,
          maxRow: 1000,
          startDate: null,
          src: null,
          endDate: null,
          isEmployee: null,
          groupId: null,
          active: false,
        },
      };

      const p1 = Source.PolicyCardByPage(list)
        .then((res) => {
          this.insuredList = res.data?.data || [];
        })
        .catch(() => {
          return Source.PolicyCardActive({
            policyId: Number(policyId),
            Id: Number(policyId),
            search: "",
            requestCorrelationId: "string",
          })
            .then((res) => {
              this.insuredList = res.data?.data || [];
            })
            .catch(() => {
              this.insuredList = [];
            });
        });

      const p2 = Source.Groups({
        id: Number(policyId),
        requestCorrelationId: "string",
      })
        .then((res) => {
          this.groupOptions = res.data?.data || [];
        })
        .catch(() => {
          this.groupOptions = [];
        });

      Promise.all([p1, p2]).finally(() => {
        this.loading = false;
        if (this.initialSelectedInsured) {
          const targetKey = this.getRowKey(this.initialSelectedInsured);
          const match = this.insuredList.find((r) => this.getRowKey(r) === targetKey);
          const selectedPerson = match || this.initialSelectedInsured;
          this.selectedSingleInsured = selectedPerson;
          this.selectedRows = [selectedPerson];
          if (this.opType === "updateInfo") {
            this.handleSingleSelect(selectedPerson);
          } else {
            this.$nextTick(() => {
              const tableRef = this.opType === "programChange" ? this.$refs.programTableRef : this.$refs.cancelTableRef;
              if (tableRef && match) {
                tableRef.toggleRowSelection(match, true);
              }
            });
          }
        }
      });
    },
    getRowKey(row) {
      if (!row) return "";
      return String(row.cardId || row.id || row.pin || `${row.insuredName || ""}_${row.cardFullNumber || ""}`);
    },
    handleSelectionChange(rows) {
      this.selectedRows = rows;
    },
    onProgramRowClick(row) {
      this.$refs.programTableRef?.toggleRowSelection(row);
    },
    onCancelRowClick(row) {
      this.$refs.cancelTableRef?.toggleRowSelection(row);
    },
    removeSelected(row) {
      const tableRef = this.opType === "programChange" ? this.$refs.programTableRef : this.$refs.cancelTableRef;
      const targetKey = this.getRowKey(row);
      if (tableRef) {
        const found = this.insuredList.find((r) => this.getRowKey(r) === targetKey);
        tableRef.toggleRowSelection(found || row, false);
      }
      this.selectedRows = this.selectedRows.filter((r) => this.getRowKey(r) !== targetKey);
    },
    clearAllSelected() {
      if (this.opType === "programChange") {
        this.$refs.programTableRef?.clearSelection();
      } else if (this.opType === "terminate") {
        this.$refs.cancelTableRef?.clearSelection();
      }
      this.selectedRows = [];
    },
    handleSingleSelect(row) {
      this.loading = true;
      Source.PolicyCardByCard({ id: row.cardId, requestCorrelationId: "string" })
        .then((res) => {
          const info = res.data?.data?.[0] || row;
          if (!info.policyId) {
            info.policyId = this.getEffectivePolicyId();
          }
          this.selectedSingleInsured = info;
        })
        .catch(() => {
          const info = { ...row };
          if (!info.policyId) {
            info.policyId = this.getEffectivePolicyId();
          }
          this.selectedSingleInsured = info;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    clearSingleSelect() {
      this.selectedSingleInsured = null;
    },
    onSingleUpdateSuccess(val) {
      if (val === 1) {
        notifyRequestSuccess({
          count: 1,
          isBulk: true,
          policyId: this.getEffectivePolicyId(),
        });
        this.$emit("success", { isBulk: true, opType: "updateInfo" });
        this.handleClose();
      } else {
        this.clearSingleSelect();
      }
    },
    isSameProgram(person) {
      if (!this.programForm.groupId || !person) return false;
      return Number(person.groupId) === Number(this.programForm.groupId);
    },
    removeSameProgramPersons() {
      if (!this.programForm.groupId) return;
      const sameRows = this.selectedRows.filter(
        (item) => Number(item.groupId) === Number(this.programForm.groupId)
      );
      const sameKeys = new Set(sameRows.map((r) => this.getRowKey(r)));
      this.selectedRows = this.selectedRows.filter((r) => !sameKeys.has(this.getRowKey(r)));
      if (this.$refs.programTableRef) {
        sameRows.forEach((r) => {
          this.$refs.programTableRef.toggleRowSelection(r, false);
        });
      }
    },
    async submitBatchProgramChange() {
      if (!this.selectedRows.length || !this.programForm.groupId || !this.programForm.startDate) {
        return;
      }

      const sameProgramRows = this.selectedRows.filter(
        (item) => Number(item.groupId) === Number(this.programForm.groupId)
      );

      if (sameProgramRows.length > 0) {
        if (sameProgramRows.length === this.selectedRows.length) {
          this.$swal({
            title: this.$t("commonWarning") || "Xəbərdarlıq",
            text: this.$t("sameProgramAllWarning"),
            icon: "warning",
            customClass: swalDialogClass(),
            buttonsStyling: false,
          });
          return;
        }

        const names = sameProgramRows
          .map((r) => r.insuredName || [r.lastName, r.firstName].filter(Boolean).join(" "))
          .slice(0, 3)
          .join(", ") + (sameProgramRows.length > 3 ? ` (+${sameProgramRows.length - 3})` : "");

        const validCount = this.selectedRows.length - sameProgramRows.length;
        const promptTemplate = this.$t("sameProgramPrompt") || "";
        const promptText = promptTemplate
          .replace("{count}", sameProgramRows.length)
          .replace("{validCount}", validCount);

        const result = await this.$swal({
          title: this.$t("commonWarning") || "Diqqət",
          html: `
            <div class="text-start">
              <p class="mb-2">${promptText}</p>
              <div class="p-2 rounded text-xs mb-2" style="background: var(--app-surface-muted, #f1f5f9); border: 1px solid var(--app-border, #e2e8f0); color: var(--app-text, #1e293b);">
                <strong>${this.$t("alreadyHasThisProgram")}:</strong> ${names}
              </div>
            </div>
          `,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: this.$t("removeAndContinue"),
          cancelButtonText: this.$t("commonCancel"),
          customClass: swalDialogClass(),
          buttonsStyling: false,
        });

        if (result.isConfirmed) {
          this.removeSameProgramPersons();
        } else {
          return;
        }
      }

      if (!this.selectedRows.length) {
        return;
      }

      this.submitting = true;
      const currentUserId = this.getUserId();
      const policyId = this.getEffectivePolicyId();
      const realDate = new Date(this.programForm.startDate);

      try {
        const allGroups = this.availablePrograms && this.availablePrograms.length
          ? this.availablePrograms
          : (this.groupOptions || []);

        const selectedGroup = allGroups.find(
          (g) => Number(g.groupId || g.id) === Number(this.programForm.groupId)
        );
        const newGroupName = selectedGroup ? (selectedGroup.groupName || selectedGroup.name || selectedGroup.label) : null;

        const rowsToProcess = this.selectedRows.length > 0 ? this.selectedRows : (this.singlePerson ? [this.singlePerson] : []);
        const items = rowsToProcess.map((item) => ({
          cardId: item.cardId,
          cardFullNumber: item.cardFullNumber,
          pin: item.pin,
          firstName: item.firstName,
          lastName: item.lastName,
          middleName: item.middleName,
          dob: item.dob,
          sex: item.sex,
          phone: item.phoneMobile || item.phone,
          address: item.address,
          isEmployee: item.isEmployee === true || item.isEmployee === 1,
          vip: item.vip,
          oldGroupId: item.groupId,
          oldGroupName: item.groupName,
          newGroupId: this.programForm.groupId,
          newGroupName: newGroupName,
          startDate: realDate,
          endDate: null,
        }));

        const bulkPayload = {
          requestCorrelationId: "string",
          policyId: Number(policyId),
          operType: 3,
          groupId: this.programForm.groupId,
          effectiveDate: realDate,
          descr: this.programForm.descr,
          createUser: currentUserId || 0,
          items: items,
        };

        const res = await Source.AccountRequestBulkInsert(bulkPayload);
        if (res.data && res.data.errors && res.data.errors.length) {
          throw new Error(res.data.errors.join(", "));
        }

        notifyRequestSuccess({
          count: items.length,
          policyId: policyId,
          isBulk: true,
        });

        this.$emit("success", { isBulk: true, opType: "programChange", count: items.length });
        this.handleClose();
      } catch (err) {
        console.error("Batch program change failed:", err);
        this.$swal({
          title: this.$t("operationFailed"),
          text: err.message || String(err),
          icon: "error",
          customClass: swalDialogClass(),
          buttonsStyling: false,
        });
      } finally {
        this.submitting = false;
      }
    },
    async submitBatchTermination() {
      if (!this.selectedRows.length || !this.cancelForm.cancelDate) {
        return;
      }
      this.submitting = true;
      const currentUserId = this.getUserId();
      const policyId = this.getEffectivePolicyId();
      const realDate = new Date(this.cancelForm.cancelDate);

      try {
        const rowsToProcess = this.selectedRows.length > 0 ? this.selectedRows : (this.singlePerson ? [this.singlePerson] : []);

        // Filter employees to check if they have linked family members (dependants)
        const employees = rowsToProcess.filter((p) => p.isEmployee === true || p.isEmployee === 1);

        if (employees.length > 0) {
          const depPromises = employees.map((emp) =>
            Source.PolicyInsuredDependants({
              id: emp.cardId,
              requestCorrelationId: "string",
            })
              .then((res) => ({
                employee: emp,
                dependants: res.data && res.data.data ? res.data.data : [],
              }))
              .catch((e) => {
                console.warn("Could not check dependants for emp:", emp.cardId, e);
                return { employee: emp, dependants: [] };
              })
          );

          const depResults = await Promise.all(depPromises);

          const selectedIds = new Set(rowsToProcess.map((r) => r.cardId));
          const selectedPins = new Set(rowsToProcess.map((r) => r.pin).filter(Boolean));

          const familyGroups = [];
          let allDeps = [];

          for (const res of depResults) {
            // Exclude dependants already in selected rows
            const unselectedDeps = (res.dependants || []).filter((d) => {
              const depId = d.dependantCardId || d.dependantInsuredId || d.cardId;
              const depPin = d.pin || d.dependantPin;
              return !selectedIds.has(depId) && (!depPin || !selectedPins.has(depPin));
            });

            if (unselectedDeps.length > 0) {
              familyGroups.push({
                employee: res.employee,
                dependants: unselectedDeps,
              });
              allDeps = allDeps.concat(unselectedDeps);
            }
          }

          if (familyGroups.length > 0) {
            this.familyGroupsToHandle = familyGroups;
            this.allDependantsFlat = allDeps;
            this.familyActionPreset = "terminateAll";
            this.globalTransferEmployee = null;
            this.groupTransferEmployees = {};

            const map = {};
            allDeps.forEach((d) => {
              const key = this.getDependantKey(d);
              map[key] = {
                action: "terminate",
                targetEmployee: null,
              };
            });
            this.dependantActionsMap = map;

            this.personToTerminate = familyGroups.length === 1 ? familyGroups[0].employee : null;
            this.dependantsToHandle = allDeps;
            this.familyWarningModalVisible = true;
            this.submitting = false;

            // Load active employees available for transfer
            const terminatingCardIds = new Set(rowsToProcess.map((r) => r.cardId));
            this.activeEmployeesForTransfer = (this.insuredList || []).filter(
              (e) => (e.isEmployee === true || e.isEmployee === 1) && !terminatingCardIds.has(e.cardId)
            );

            Source.PolicyCardByPage({
              requestCorrelationId: "string",
              data: {
                policyId: Number(policyId),
                isEmployee: true,
                active: null,
                currentPage: 1,
                maxRow: 1000,
                search: "",
              },
            }).then((empRes) => {
              const list = (empRes.data?.data || []).filter(
                (e) => (e.isEmployee === true || e.isEmployee === 1) && !terminatingCardIds.has(e.cardId)
              );
              if (list.length > 0) {
                this.activeEmployeesForTransfer = list;
              }
            }).catch(() => {});

            return;
          }
        }

        // No dependants found: direct execution
        await this.executeStandardTermination(rowsToProcess, realDate, policyId, currentUserId);
      } catch (err) {
        console.error("Batch termination failed:", err);
        this.$swal({
          title: this.$t("operationFailed"),
          text: err.message || String(err),
          icon: "error",
          customClass: swalDialogClass(),
          buttonsStyling: false,
        });
      } finally {
        this.submitting = false;
      }
    },
    async executeStandardTermination(rowsToProcess, realDate, policyId, currentUserId) {
      const items = rowsToProcess.map((item) => ({
        cardId: item.cardId,
        cardFullNumber: item.cardFullNumber,
        pin: item.pin,
        firstName: item.firstName,
        lastName: item.lastName,
        middleName: item.middleName,
        dob: item.dob,
        sex: item.sex,
        phone: item.phoneMobile || item.phone,
        address: item.address,
        isEmployee: item.isEmployee === true || item.isEmployee === 1,
        vip: item.vip,
        oldGroupId: item.groupId,
        oldGroupName: item.groupName,
        newGroupId: null,
        newGroupName: null,
        startDate: null,
        endDate: realDate,
      }));

      const bulkPayload = {
        requestCorrelationId: "string",
        policyId: Number(policyId),
        operType: 2,
        groupId: null,
        effectiveDate: realDate,
        descr: this.cancelForm.descr,
        createUser: currentUserId || 0,
        items: items,
      };

      const res = await Source.AccountRequestBulkInsert(bulkPayload);
      if (res.data && res.data.errors && res.data.errors.length) {
        throw new Error(res.data.errors.join(", "));
      }

      notifyRequestSuccess({
        count: items.length,
        policyId: policyId,
        isBulk: true,
      });

      this.$emit("success", { isBulk: true, opType: "terminate", count: items.length });
      this.handleClose();
    },
    getDependantKey(dep) {
      if (!dep) return "";
      return String(dep.dependantCardId || dep.dependantInsuredId || dep.cardId || dep.pin || dep.dependantId || "");
    },
    getGroupKey(group) {
      if (!group || !group.employee) return "";
      return String(group.employee.cardId || group.employee.pin || "");
    },
    getDepState(dep) {
      const key = this.getDependantKey(dep);
      if (!this.dependantActionsMap[key]) {
        this.dependantActionsMap[key] = {
          action: "terminate",
          targetEmployee: null,
        };
      }
      return this.dependantActionsMap[key];
    },
    getDepAction(dep) {
      return this.getDepState(dep).action || "terminate";
    },
    setDependantAction(dep, action) {
      const state = this.getDepState(dep);
      state.action = action;
      if (action === "reassign" && !state.targetEmployee) {
        // Find if this dependant belongs to a group with an assigned target or global target
        const group = (this.familyGroupsToHandle || []).find((g) =>
          (g.dependants || []).some((d) => this.getDependantKey(d) === this.getDependantKey(dep))
        );
        const groupTarget = group ? this.getGroupTargetEmployee(group) : null;
        state.targetEmployee = groupTarget || this.globalTransferEmployee || null;
      }
      this.familyActionPreset = "custom";
    },
    setAllDependantsAction(action) {
      (this.allDependantsFlat || []).forEach((dep) => {
        const state = this.getDepState(dep);
        state.action = action;
        if (action === "reassign" && !state.targetEmployee) {
          state.targetEmployee = this.globalTransferEmployee || null;
        }
      });
      this.familyActionPreset = action === "terminate" ? "terminateAll" : (action === "reassign" ? "reassignAll" : "custom");
    },
    applyGlobalPreset(preset) {
      this.familyActionPreset = preset;
      if (preset === "terminateAll") {
        this.setAllDependantsAction("terminate");
      }
    },
    onGlobalTransferEmployeeChange(val) {
      if (!val) return;
      (this.allDependantsFlat || []).forEach((dep) => {
        const state = this.getDepState(dep);
        if (state.action === "reassign") {
          state.targetEmployee = val;
        }
      });
      // Also update all groups
      (this.familyGroupsToHandle || []).forEach((group) => {
        const gKey = this.getGroupKey(group);
        this.groupTransferEmployees[gKey] = val;
      });
    },
    getGroupTargetEmployee(group) {
      const gKey = this.getGroupKey(group);
      return this.groupTransferEmployees[gKey] || this.globalTransferEmployee || null;
    },
    setGroupTargetEmployee(group, targetEmp) {
      const gKey = this.getGroupKey(group);
      this.groupTransferEmployees[gKey] = targetEmp;
      (group.dependants || []).forEach((dep) => {
        const state = this.getDepState(dep);
        if (state.action === "reassign") {
          state.targetEmployee = targetEmp;
        }
      });
    },
    isGroupHavingReassign(group) {
      return (group.dependants || []).some((dep) => this.getDepAction(dep) === "reassign");
    },
    setGroupAction(group, action) {
      const gTarget = this.getGroupTargetEmployee(group) || this.globalTransferEmployee || null;
      (group.dependants || []).forEach((dep) => {
        const state = this.getDepState(dep);
        state.action = action;
        if (action === "reassign" && !state.targetEmployee) {
          state.targetEmployee = gTarget;
        }
      });
      this.familyActionPreset = "custom";
    },
    getEffectiveDepTarget(dep, group) {
      const state = this.getDepState(dep);
      if (state.targetEmployee && state.targetEmployee.cardId) {
        return state.targetEmployee;
      }
      const groupTarget = group ? this.getGroupTargetEmployee(group) : null;
      if (groupTarget && groupTarget.cardId) {
        return groupTarget;
      }
      return this.globalTransferEmployee || null;
    },
    isGroupAllAction(group, action) {
      if (!group || !group.dependants || !group.dependants.length) return false;
      return group.dependants.every((d) => this.getDepAction(d) === action);
    },
    isAllDependantsAction(action) {
      if (!this.allDependantsFlat || !this.allDependantsFlat.length) return false;
      return this.allDependantsFlat.every((d) => this.getDepAction(d) === action);
    },
    async executeFamilyTerminationAction() {
      if (!this.familyGroupsToHandle || !this.familyGroupsToHandle.length) return;
      this.submitting = true;
      const currentUserId = this.getUserId();
      const policyId = this.getEffectivePolicyId();
      const realDate = new Date(this.cancelForm.cancelDate || new Date());
      const rowsToProcess = this.selectedRows.length > 0 ? this.selectedRows : (this.singlePerson ? [this.singlePerson] : []);

      try {
        const depsToTerminate = [];
        const reassignMapByTarget = new Map();

        // Separate dependants by action
        for (const group of this.familyGroupsToHandle) {
          for (const dep of group.dependants || []) {
            const action = this.getDepAction(dep);
            if (action === "terminate") {
              depsToTerminate.push(dep);
            } else if (action === "reassign") {
              const targetEmp = this.getEffectiveDepTarget(dep, group);
              if (!targetEmp || !targetEmp.cardId) {
                this.$swal({
                  title: this.$t("commonWarning") || "Diqqət",
                  text: `Zəhmət olmasa "${dep.dependantInsuredName || dep.insuredName}" adlı ailə üzvünün bağlanacağı işçini seçin.`,
                  icon: "warning",
                  customClass: swalDialogClass(),
                  buttonsStyling: false,
                });
                this.submitting = false;
                return;
              }
              const targetCardId = targetEmp.cardId;
              if (!reassignMapByTarget.has(targetCardId)) {
                reassignMapByTarget.set(targetCardId, {
                  targetEmployee: targetEmp,
                  dependants: [],
                });
              }
              reassignMapByTarget.get(targetCardId).dependants.push({
                dependantInsuredName: dep.dependantInsuredName || dep.insuredName,
                insuredDependantId: dep.dependantInsuredId || dep.dependantCardId || dep.cardId,
              });
            }
          }
        }

        // 1. Process Reassignments if any
        if (reassignMapByTarget.size > 0) {
          for (const [targetCardId, data] of reassignMapByTarget.entries()) {
            const relatePayload = {
              requestCorrelationId: "string",
              cardId: targetCardId,
              dependants: data.dependants,
            };
            await Source.NewPolicyInsuredDependantRequest(relatePayload);
          }
        }

        // 2. Process Terminations: ONLY selected employees are sent in bulk Xitam (family members are not sent to Xitam)
        const items = rowsToProcess.map((p) => ({
          cardId: p.cardId,
          cardFullNumber: p.cardFullNumber,
          pin: p.pin,
          firstName: p.firstName,
          lastName: p.lastName,
          middleName: p.middleName,
          dob: p.dob,
          sex: p.sex,
          phone: p.phoneMobile || p.phone,
          address: p.address,
          isEmployee: p.isEmployee === true || p.isEmployee === 1,
          vip: p.vip,
          oldGroupId: p.groupId,
          oldGroupName: p.groupName,
          newGroupId: null,
          newGroupName: null,
          startDate: null,
          endDate: realDate,
        }));

        const bulkPayload = {
          requestCorrelationId: "string",
          policyId: Number(policyId),
          operType: 2,
          groupId: null,
          effectiveDate: realDate,
          descr: this.cancelForm.descr || "İşçi(lər)in xitamı",
          createUser: currentUserId || 0,
          items: items,
        };

        const res = await Source.AccountRequestBulkInsert(bulkPayload);
        if (res.data && res.data.errors && res.data.errors.length) {
          throw new Error(res.data.errors.join(", "));
        }

        const totalReassigned = this.dependantsToReassignCount;
        let successMsg = `${rowsToProcess.length} nəfər işçi üçün xitam sorğusu göndərildi.`;
        if (totalReassigned > 0) {
          successMsg += ` ${totalReassigned} nəfər ailə üzvü yeni sığortalıya bağlandı.`;
        }

        notifyRequestSuccess({
          count: items.length,
          policyId: policyId,
          message: successMsg,
          isBulk: true,
        });

        this.familyWarningModalVisible = false;
        this.$emit("success", { isBulk: true, opType: "terminate", count: items.length });
        this.handleClose();
      } catch (err) {
        console.error("Family termination action failed:", err);
        this.$swal({
          title: this.$t("operationFailed"),
          text: err.message || String(err),
          icon: "error",
          customClass: swalDialogClass(),
          buttonsStyling: false,
        });
      } finally {
        this.submitting = false;
      }
    },
    handleClose() {
      this.visible = false;
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.request-op-modal {
  min-height: 320px;
}

.request-op-toolbar {
  margin-bottom: 0.75rem;
}

.request-op-search-input :deep(.el-input__inner) {
  height: 38px;
  font-size: 0.875rem;
}

.request-op-table {
  width: 100%;
}

.request-op-table :deep(.el-table__row) {
  cursor: pointer;
}

.request-op-form-card {
  background: var(--app-surface-soft, rgba(120, 130, 145, 0.05));
  border: 1px solid var(--app-border);
  border-radius: 8px;
}

.badge--info {
  background: color-mix(in srgb, var(--bs-info) 18%, transparent);
  color: var(--bs-info);
  padding: 0.35rem 0.6rem;
  border-radius: 6px;
  font-weight: 600;
}

.badge--danger {
  background: rgba(220, 38, 38, 0.15);
  color: #dc2626;
  padding: 0.35rem 0.6rem;
  border-radius: 6px;
  font-weight: 600;
}

.badge--muted {
  background: rgba(120, 130, 145, 0.15);
  color: var(--app-text);
  padding: 0.35rem 0.6rem;
  border-radius: 6px;
  font-weight: 600;
}

.request-op-sidebar {
  background: var(--app-surface-soft, rgba(120, 130, 145, 0.05));
  border: 1px solid var(--app-border);
  border-radius: 8px;
  min-height: 480px;
}

.selected-cards-list {
  max-height: 430px;
  overflow-y: auto;
  padding-right: 4px;
}

.selected-cards-list::-webkit-scrollbar {
  width: 5px;
}

.selected-cards-list::-webkit-scrollbar-thumb {
  background: rgba(120, 130, 145, 0.3);
  border-radius: 4px;
}

.selected-person-card {
  background: var(--app-surface-elevated, #ffffff);
  border: 1px solid var(--app-border, #e5e7eb);
  border-radius: 8px;
  transition: all 0.2s ease;
}

.selected-person-card:hover {
  background: var(--app-surface-soft, #fafbfc);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.selected-person-card--warning {
  border-left: 3.5px solid #f59e0b !important;
}

.selected-person-card--danger {
  border-left: 3.5px solid #ef4444 !important;
}

.selected-person-card__name {
  color: var(--app-heading, #111827);
  line-height: 1.35;
  font-weight: 600;
}

.badge-tag {
  background: var(--app-surface-muted, #f1f5f9);
  color: var(--app-text, #475569);
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 0.6875rem;
  font-weight: 500;
}

.text-2xs {
  font-size: 0.6875rem;
}

.selected-person-card__remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--app-text, #94a3b8);
  cursor: pointer;
  padding: 2px;
  border-radius: 4px;
  font-size: 1rem;
  line-height: 1;
  transition: all 0.15s ease;
}

.selected-person-card__remove:hover {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
}

.badge--warning {
  background: rgba(245, 158, 11, 0.15);
  color: #d97706;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  font-weight: 600;
}

.btn-clear-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  font-size: 0.72rem;
  font-weight: 600;
  border-radius: 6px;
  border: 1px solid rgba(239, 68, 68, 0.3);
  background: rgba(239, 68, 68, 0.08);
  color: #ef4444;
  cursor: pointer;
  transition: all 0.2s ease;
  line-height: 1;
}

.btn-clear-pill:hover {
  background: rgba(239, 68, 68, 0.2);
  border-color: rgba(239, 68, 68, 0.55);
  color: #dc2626;
  transform: translateY(-1px);
}

.btn-clear-pill:active {
  transform: translateY(0);
}

/* Warning banner for matching programs */
.request-op-warning-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 8px;
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.35);
  box-shadow: 0 1px 4px rgba(245, 158, 11, 0.05);
}

.warning-banner-content {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  flex: 1;
}

.warning-banner-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 5px;
  background: rgba(245, 158, 11, 0.2);
  color: #d97706;
  font-size: 0.75rem;
  flex-shrink: 0;
}

.warning-banner-text {
  font-size: 0.73rem;
  font-weight: 500;
  color: var(--app-text, #1e293b);
  line-height: 1.25;
}

.warning-count {
  font-weight: 700;
  color: #d97706;
}

.warning-banner-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  font-size: 0.7rem;
  font-weight: 600;
  border-radius: 6px;
  border: 1px solid rgba(245, 158, 11, 0.45);
  background: rgba(245, 158, 11, 0.16);
  color: #b45309;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.2s ease;
  line-height: 1;
}

.warning-banner-btn:hover {
  background: rgba(245, 158, 11, 0.3);
  border-color: rgba(245, 158, 11, 0.65);
  color: #92400e;
  transform: translateY(-1px);
}

.warning-banner-btn:active {
  transform: translateY(0);
}

/* Dark mode explicit overrides */
:global(html[data-theme="dark"]) .btn-clear-pill {
  background: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.35);
  color: #f87171;
}

:global(html[data-theme="dark"]) .btn-clear-pill:hover {
  background: rgba(239, 68, 68, 0.28);
  border-color: rgba(239, 68, 68, 0.6);
  color: #fca5a5;
}

:global(html[data-theme="dark"]) .request-op-warning-banner {
  background: rgba(245, 158, 11, 0.14);
  border-color: rgba(245, 158, 11, 0.3);
}

:global(html[data-theme="dark"]) .warning-banner-icon {
  background: rgba(245, 158, 11, 0.25);
  color: #fbbf24;
}

:global(html[data-theme="dark"]) .warning-banner-text {
  color: #e2e8f0;
}

:global(html[data-theme="dark"]) .warning-count {
  color: #fbbf24;
}

:global(html[data-theme="dark"]) .warning-banner-btn {
  background: rgba(245, 158, 11, 0.22);
  border-color: rgba(245, 158, 11, 0.45);
  color: #fcd34d;
}

:global(html[data-theme="dark"]) .warning-banner-btn:hover {
  background: rgba(245, 158, 11, 0.35);
  border-color: rgba(245, 158, 11, 0.7);
  color: #fef08a;
}

:global(html[data-theme="dark"]) .selected-person-card {
  background: #1c232b;
  border-color: #2a3340;
}

:global(html[data-theme="dark"]) .selected-person-card:hover {
  background: #222a35;
}

:global(html[data-theme="dark"]) .selected-person-card__name {
  color: #eef2f6;
}

:global(html[data-theme="dark"]) .selected-person-card__remove {
  color: #9aa6b2;
}

:global(html[data-theme="dark"]) .selected-person-card__remove:hover {
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
}

/* Family Termination Warning Dialog Styles (Horizontal Side-by-Side) */
.family-warning-banner {
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.25);
  border-radius: 10px;
}

.family-warning-banner__sub {
  color: var(--app-text, #64748b);
}

.family-members-box__label {
  color: var(--app-heading, #334155);
}

.family-summary-box {
  background: var(--app-surface-soft, #f8fafc);
  border: 1px solid var(--app-border, #e2e8f0);
}

.family-group-panel {
  background: var(--app-surface-soft, #f8fafc);
  border: 1px solid var(--app-border, #e2e8f0);
  transition: all 0.2s ease;
}

.group-transfer-picker {
  background: var(--app-surface, #ffffff);
  border: 1px solid var(--app-border, #cbd5e1);
}

.dependant-row-card {
  background: var(--app-surface, #ffffff);
  border: 1px solid var(--app-border, #e2e8f0);
  transition: all 0.2s ease;
}

.dependant-row-card--terminate {
  border-color: rgba(239, 68, 68, 0.4) !important;
  background: rgba(239, 68, 68, 0.04) !important;
}

.dependant-row-card--reassign {
  border-color: rgba(59, 130, 246, 0.4) !important;
  background: rgba(59, 130, 246, 0.04) !important;
}

/* Modern Segmented Switch */
.family-action-switch {
  display: inline-flex;
  align-items: center;
  background: var(--app-surface-soft, #f1f5f9);
  border: 1px solid var(--app-border, #cbd5e1);
  padding: 3px;
  border-radius: 9px;
  gap: 3px;
  user-select: none;
}

.family-action-switch--sm {
  padding: 2px;
  border-radius: 7px;
  gap: 2px;
}

:global(html[data-theme="dark"]) .family-action-switch {
  background: #141c26;
  border-color: #2b3644;
}

.family-switch-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--app-text, #64748b);
  font-size: 0.74rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  line-height: 1.2;
  white-space: nowrap;
}

.family-action-switch--sm .family-switch-btn {
  font-size: 0.7rem;
  padding: 3px 8px;
  border-radius: 5px;
}

.family-switch-btn:hover {
  color: var(--app-heading, #1e293b);
  background: rgba(255, 255, 255, 0.6);
}

:global(html[data-theme="dark"]) .family-switch-btn {
  color: #94a3b8;
}

:global(html[data-theme="dark"]) .family-switch-btn:hover {
  color: #f1f5f9;
  background: rgba(255, 255, 255, 0.08);
}

/* Active Danger (Xitam) */
.family-switch-btn--danger.is-active {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
  color: #ffffff !important;
  box-shadow: 0 2px 6px rgba(239, 68, 68, 0.4);
  font-weight: 700;
}

/* Active Primary (Bağla) */
.family-switch-btn--primary.is-active {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
  color: #ffffff !important;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.4);
  font-weight: 700;
}

/* Header & Group Action Pills */
.btn-family-pill,
.btn-group-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  font-size: 0.72rem;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  line-height: 1;
  border: 1px solid transparent;
}

.btn-group-pill {
  padding: 2px 8px;
  font-size: 0.7rem;
}

.btn-family-pill--danger,
.btn-group-pill--danger {
  background: rgba(239, 68, 68, 0.12);
  border-color: rgba(239, 68, 68, 0.35);
  color: #ef4444;
}

.btn-family-pill--danger:hover,
.btn-group-pill--danger:hover {
  background: #ef4444;
  border-color: #ef4444;
  color: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(239, 68, 68, 0.35);
}

.btn-family-pill--primary,
.btn-group-pill--primary {
  background: rgba(37, 99, 235, 0.12);
  border-color: rgba(37, 99, 235, 0.35);
  color: #2563eb;
}

.btn-family-pill--primary:hover,
.btn-group-pill--primary:hover {
  background: #2563eb;
  border-color: #2563eb;
  color: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.35);
}

:global(html[data-theme="dark"]) .btn-family-pill--danger,
:global(html[data-theme="dark"]) .btn-group-pill--danger {
  background: rgba(239, 68, 68, 0.18);
  border-color: rgba(239, 68, 68, 0.4);
  color: #f87171;
}

:global(html[data-theme="dark"]) .btn-family-pill--primary,
:global(html[data-theme="dark"]) .btn-group-pill--primary {
  background: rgba(59, 130, 246, 0.18);
  border-color: rgba(59, 130, 246, 0.4);
  color: #60a5fa;
}

.family-option-card {
  padding: 0.75rem 0.875rem;
  border-radius: 10px;
  border: 1px solid var(--app-border, #e2e8f0);
  background: var(--app-surface, #ffffff);
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.family-option-card:hover {
  border-color: var(--app-border-strong, #cbd5e1);
  background: var(--app-surface-soft, #f8fafc);
}

.family-option-card--danger.is-active {
  border-color: #ef4444 !important;
  background: rgba(239, 68, 68, 0.1) !important;
}

.family-option-card--info.is-active {
  border-color: #0ea5e9 !important;
  background: rgba(14, 165, 233, 0.1) !important;
}

.family-option-card--primary.is-active {
  border-color: #3b82f6 !important;
  background: rgba(59, 130, 246, 0.1) !important;
}

.family-option-title {
  font-size: 0.84rem;
  font-weight: 700;
}

.family-option-title--danger {
  color: #ef4444 !important;
}

.family-option-title--info {
  color: #0284c7 !important;
}

.family-option-title--primary {
  color: #3b82f6 !important;
}

.family-option-desc {
  font-size: 0.775rem;
  color: var(--app-text, #64748b);
  line-height: 1.35;
}
</style>
